import React from 'react';
import { POLICY } from './data.jsx';
import * as T from '../time.js';
import { api } from '../api.js';
import { Empty, Icon, Loading, PH, Panel } from './components.jsx';
import { PART_LABEL } from './leave-common.jsx';

// leave-absence.jsx — 부재 현황(계획 문서 5.2 2-2c): 그 달에 걸친 승인된 휴가를 날마다 보인다.
// 누구의 부재가 보이는지와 종류 이름은 서버가 정한다(type_name이 null이면 '휴가' — 병가 같은 종류는 민감 정보). 화면은 범위를 판단하지 않는다.

const { useState: uS, useEffect: uE } = React;
const WD = ['월', '화', '수', '목', '금', '토', '일'];
const pad = n => String(n).padStart(2, '0');
const box = { padding: '6px 8px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 12.5 };

export function AbsenceBoard({ onToast }) {
  const [month, setMonth] = uS(T.todayYmd().slice(0, 7));   // 앱 시간대의 이번 달
  const [departmentId, setDepartmentId] = uS('');
  const [data, setData] = uS(null);
  const [holidays, setHolidays] = uS({});
  const [y, m] = month.split('-').map(Number);
  uE(() => {
    let current = true;
    api.get(`/api/v1/leave/absences?month=${month}${departmentId ? '&departmentId=' + departmentId : ''}`)
      .then(r => { if (current) setData(r); })
      .catch(e => { if (current) { setData(null); onToast(e.message); } });
    return () => { current = false; };
  }, [month, departmentId, onToast]);
  uE(() => {
    let current = true;
    api.get('/api/v1/leave/holidays?year=' + y)
      .then(r => { if (current) setHolidays(Object.fromEntries(r.holidays.map(h => [h.date, h.name]))); })
      .catch(() => { if (current) setHolidays({}); });   // 표시용 — 부재 목록은 서버가 이미 정했다
    return () => { current = false; };
  }, [y]);
  const count = T.daysInMonth(y, m);
  const lead = (T.dowOfYmd(`${month}-01`) + 6) % 7;   // 월요일부터
  const ymd = d => `${month}-${pad(d)}`;
  // 쉬는 날 — 업무 정책의 쉬는 요일(ISO, 서버 /policy)과 공휴일(계획 문서 5.4 4c)
  const offDay = d => POLICY.leave.daysOff.includes(T.dowOfYmd(ymd(d)) || 7) || Boolean(holidays[ymd(d)]);
  // 그날 걸친 부재 — 쉬는 날(주말·공휴일)은 휴가 일수에 들지 않으므로 비운다
  const on = d => (offDay(d) ? [] : (data?.absences ?? []).filter(a => a.start_date <= ymd(d) && ymd(d) <= a.end_date));
  const shift = k => { const t = T.shiftMonth(y, m, k); setMonth(`${t.y}-${pad(t.m)}`); };
  const scope = data?.scope;
  return (
    <Panel>
      <PH title="부재 현황" sub={data ? `${y}년 ${m}월 · ${data.absences.length}건 · 승인된 휴가` : ''}
        right={<span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          {scope && (scope.all || scope.departments.length > 1) && (
            <select style={box} value={departmentId} onChange={e => setDepartmentId(e.target.value)} aria-label="부재 부서">
              <option value="">{scope.all ? '전체' : '볼 수 있는 부서 전체'}</option>
              {scope.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          <button className="ico" aria-label="부재 이전 달" onClick={() => shift(-1)}><Icon name="chevron-left" size={14} /></button>
          <input type="month" style={box} value={month} onChange={e => e.target.value && setMonth(e.target.value)} aria-label="부재 달" />
          <button className="ico" aria-label="부재 다음 달" onClick={() => shift(1)}><Icon name="chevron-right" size={14} /></button>
        </span>} />
      {!data ? <Loading title="불러오는 중" /> : (
        <div style={{ padding: 14 }} data-absence-board>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5, marginBottom: 6 }}>
            {WD.map((d, i) => <div key={d} className="mono" style={{ textAlign: 'center', fontSize: 9.5, fontWeight: 600, color: i === 6 ? 'var(--crit)' : i === 5 ? 'var(--info)' : 'var(--ink-3)' }}>{d}</div>)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5 }}>
            {Array.from({ length: lead }, (_, i) => <span key={'b' + i} />)}
            {Array.from({ length: count }, (_, i) => i + 1).map(d => {
              const list = on(d);
              const holiday = holidays[ymd(d)];
              return (
                <div key={d} data-day={ymd(d)} style={{ minHeight: 64, padding: 6, border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)',
                  background: offDay(d) ? 'var(--surface)' : 'var(--surface-2)', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span className="mono" style={{ fontSize: 11, fontWeight: 600, color: holiday ? 'var(--crit)' : offDay(d) ? 'var(--ink-3)' : 'var(--ink-2)' }}>
                    {d}{holiday ? ` ${holiday}` : ''}</span>
                  {list.slice(0, 3).map(a => (
                    <span key={a.id} data-absence={a.id} title={`${a.user_name} · ${a.department_name ?? ''}`}
                      style={{ fontSize: 10.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.user_name} <span style={{ color: 'var(--ink-3)' }}>{a.type_name ?? '휴가'}{a.start_date === a.end_date && PART_LABEL[a.day_part] ? ' ' + PART_LABEL[a.day_part] : ''}</span>
                    </span>
                  ))}
                  {list.length > 3 && <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>+{list.length - 3}명</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Panel>
  );
}
